import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ClientAddress } from '@/app/(main)/client-detail/edit-main/components/ClientAdress';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import type { RelatedPassengerBasePath } from '@/types/clientsApiTypes';
import { MAILING_FIELDS } from '../../const/passenger';
import { getAddressPayload } from '../../utils/getAddressPayload';

type RelatedPassengerMailingAddressProps = {
  basePath: RelatedPassengerBasePath;
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
};

export const RelatedPassengerMailingAddress = ({ basePath, onUpdateFormData }: RelatedPassengerMailingAddressProps) => {
  const { watch, setValue, getValues } = useFormContext<CreateClientFormData>();
  const primaryPassenger = getValues('primary_passenger.client');

  const sameAsPrimary = watch(`${basePath}.client.same_as_primary`);

  const handleEmptyMailingAddress = () => {
    const mailingPayload: Record<string, string> = {};
    MAILING_FIELDS.forEach((field) => {
      setValue(`${basePath}.client.${field}`, '', { shouldValidate: true });
      mailingPayload[field] = '';
    });
    onUpdateFormData?.(`${basePath}.client`, mailingPayload);
  };

  const handleSetMailingAddress = () => {
    const mailingPayload: Record<string, string> = {};
    MAILING_FIELDS.forEach((field) => {
      const value = primaryPassenger?.[field] ?? '';
      setValue(`${basePath}.client.${field}`, value, { shouldValidate: true });
      mailingPayload[field] = value;
    });
    onUpdateFormData?.(`${basePath}.client`, mailingPayload);
  };

  const handleChangeSameAsPrimary = (checked: boolean) => {
    setValue(`${basePath}.client.same_as_primary`, checked);

    if (checked) {
      handleSetMailingAddress();
    } else {
      handleEmptyMailingAddress();
    }
  };

  const selectPlaceAddress = (address: { [key: string]: string }) => {
    const addressPayload = getAddressPayload(address);
    onUpdateFormData?.(`${basePath}.client`, { ...addressPayload });
  };

  return (
    <div className="mt-4">
      <ClientAddress
        intent="primaryWhite"
        selectPlaceAddress={selectPlaceAddress}
        onBlur={(e) => onUpdateFormData?.(e.target.name, e.target.value)}
        toggleConfig={{
          label: 'Same as primary passenger',
          visible: true,
          value: !!sameAsPrimary,
          onChange: () => handleChangeSameAsPrimary(!sameAsPrimary),
        }}
        fields={{
          address_line1: {
            label: 'Address Line 1',
            placeholder: 'Street address, P.O. box',
            required: false,
            name: `${basePath}.client.client_mailing_address`,
          },
          address_line2: {
            label: 'Address Line 2',
            placeholder: 'Apartment, suite, unit, building',
            name: `${basePath}.client.client_mailing_address_line_2`,
          },
          city: {
            label: 'City',
            placeholder: 'Enter city',
            required: false,
            name: `${basePath}.client.client_mailing_city`,
          },
          state: {
            label: 'State/Province',
            placeholder: 'Select state or province',
            required: false,
            name: `${basePath}.client.client_mailing_state`,
          },
          zip: {
            label: 'Zip Code',
            placeholder: 'Enter ZIP or postal code',
            required: false,
            name: `${basePath}.client.zip_code`,
          },
          country: {
            label: 'Country',
            placeholder: 'Select country',
            required: false,
            name: `${basePath}.client.client_mailing_country`,
          },
        }}
      />
    </div>
  );
};
